import React from 'react';

import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { Modal, type ModalProps } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import { HomepageProductCard } from '@/app/[locale]/(home)/components/HomepageProductCard';
import styles from '@/app/[locale]/(home)/components/SuggestedForYou/styles.module.css';
import { InfiniteScroll } from '@/components/InfiniteScroll';
import { HomePageModuleNames } from '@/modules/activityTracker/constants';
import { useSuggestedForYouProducts } from '@/modules/explore/hooks/useSuggestedForYouProducts';
import { getProductSizeName } from '@/modules/sizes/helpers';

type Props = Pick<ModalProps, 'isOpen' | 'setOpen'> & {
  onProductClick: (
    selectedProductId: number,
    selectedProductName: string,
    selectedProductPosition: number
  ) => void;
};

export function SuggestedForYouModal({
  isOpen,
  setOpen,
  onProductClick,
}: Props) {
  const tHome = useTranslations('home');
  const tCommon = useTranslations('common');
  const {
    data: products,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useSuggestedForYouProducts();

  return (
    <Modal
      wide
      isOpen={isOpen}
      setOpen={setOpen}
      title={tHome('SuggestedForYou')}
      testId={`${HomePageModuleNames.SuggestedForYou}__modal`}
      content={() => (
        <div className={styles.modalProductListContainer}>
          <InfiniteScroll
            onEndReached={fetchNextPage}
            loading={isFetching}
            hasMore={hasNextPage}
            LoadingComponent={<LoadingBall />}
          >
            <ul className={styles.modalProductList}>
              {products.map((product, index) => (
                <li key={product.id}>
                  <HomepageProductCard
                    id={product.id}
                    isLiked={product.is_liked}
                    isBoosted={product.is_boosted}
                    preview={product.preview}
                    productHref={`${product.slug}?moduleOrigin=${HomePageModuleNames.SuggestedForYou}`}
                    pictures={product.pictures}
                    displayPrice={product.pricing.display_price}
                    hasVideo={product.has_video}
                    size={
                      product.sizes &&
                      getProductSizeName(product.sizes, {
                        multipleSizes: tCommon('MultipleSizes'),
                      })
                    }
                    handleClick={() =>
                      onProductClick(product.id, product.slug, index)
                    }
                    brandName={product.brand_name}
                  />
                </li>
              ))}
            </ul>
          </InfiniteScroll>
        </div>
      )}
    />
  );
}
